import { useEffect } from 'react';

import type { CloseableNotificationType } from '../shared/types/NotificationTypes';
import { getMinutesRemaining } from '../utils/date/getMinutesRemaining';
import { getTimeDifference } from '../utils/date/getTimeDifference';
import { getExpiresAtUtc } from '../utils/session/selectors';
import type { CheckoutSessionsResponse } from '../services/commonCheckout/types/CheckoutSessionsResponse';
import shouldTriggerReminder from '../utils/capabilities/shouldTriggerReminder';

type SessionExpirationReminderProps = {
  notify: (args: CloseableNotificationType) => void;
  originalCheckoutSessionResponse: CheckoutSessionsResponse;
};

export default function useSessionExpirationReminder({
  notify,
  originalCheckoutSessionResponse,
}: SessionExpirationReminderProps) {
  if (!shouldTriggerReminder(originalCheckoutSessionResponse)) {
    return;
  }

  useEffect(() => {
    // NOTE: need to use ReturnType instead of NodeJS.Timeout because jest assumes that setTimeout
    // returns a number and is not able to assign it to NodeJS.Timeout
    let timeoutId: ReturnType<typeof setTimeout>;

    const expiresAt = getExpiresAtUtc(
      originalCheckoutSessionResponse,
    );
    const timeDifference = getTimeDifference(
      new Date(expiresAt),
      new Date(new Date().toISOString()),
    );

    if (expiresAt && timeDifference) {
      timeoutId = setTimeout(
        () => {
          const curentTimeDifference = getTimeDifference(
            new Date(expiresAt),
            new Date(new Date().toISOString()),
          );
          const minutesRemaining = getMinutesRemaining(
            curentTimeDifference,
          );
          notify({
            severity: 'warning',
            title: `Your session will expire in ${minutesRemaining} ${
              minutesRemaining === 1 ? 'minute' : 'minutes'
            }`,
          });
        },
        Math.max(timeDifference - 300000, 0),
      );
    }

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, []);
}
